'use client';

import { useState, type JSX, useEffect } from 'react';

import clsx from 'clsx';
import NextImage from 'next/image';

import { testIds } from '@/components/constants/testIds';
import { composeHeightClasses } from '@/components/functions/composeHeightClasses';
import { composeRadiusClasses } from '@/components/functions/composeRadiusClasses';
import { composeSizeClasses } from '@/components/functions/composeSizeClasses';
import { composeWidthClasses } from '@/components/functions/composeWidthClasses';
import type { Adaptive } from '@/components/types/Adaptive';
import type { Height } from '@/components/types/Height';
import type { Radius } from '@/components/types/Radius';
import type { Size } from '@/components/types/Size';
import type { Width } from '@/components/types/Width';
import { componentsConfig } from '@/config/componentsConfig';

type ImageProps = {
    src: string;
    width?: Adaptive<Width>;
    height?: Adaptive<Height>;
    size?: Adaptive<Size>;
    placeholder?: string;
    radius?: Adaptive<Radius>;
    alt?: string;
    title?: string;
    containerClassName?: string;
    className?: string;
    testId?: string;
};

export default function Image({
    src,
    width,
    height,
    size,
    placeholder = componentsConfig.image.placeholder,
    radius = componentsConfig.image.radius,
    alt,
    title,
    containerClassName,
    className,
    testId = testIds.image.base,
}: ImageProps): JSX.Element {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const [imgSrc, setImgSrc] = useState(src);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        setImgSrc(src);
    }, [src]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Main Component UI
    // ----------------------------------------------------------------------------------------------------
    return (
        <div
            data-testid={testId}
            className={clsx(
                'relative',
                width && composeWidthClasses(width),
                height && composeHeightClasses(height),
                size && composeSizeClasses(size),
                containerClassName
            )}
        >
            <NextImage
                data-testid={testIds.image.image}
                src={imgSrc}
                alt={alt ?? ''}
                title={title}
                className={clsx(className, composeRadiusClasses(radius))}
                placeholder="blur"
                blurDataURL={placeholder}
                fill
                onError={() => setImgSrc(placeholder)}
            />
        </div>
    );
}
